import {
  CreateTagGroupForm,
  TagGroupDto,
  UpdateTagGroupForm
} from "@closerplatform/spinner-openapi";
import { FieldState, FormState } from "formstate";
import { isEqual } from "lodash";
import { calculateWorkingHours, getWorkingWeekIntervals } from "@fidget/common/utils/getWorkingHours";
import {
  validateArrayNonEmpty,
  validateNoWhitespace,
  validateRequired
} from "@fidget/common/utils/validatorsFormState";
import {
  ExistingTagGroupFormFields,
  ExistingTagGroupFormType,
  IMessageWidgets,
  NewTagGroupFormFields,
  NewTagGroupFormType
} from "./types";
import {
  IMessageWidgetConfig,
  MessageWidgetConfig,
  parseMessageWidgetConfig
} from "../../../../models/messageWidgets/MessageWidgetConfig";
import { SpinnerService } from "../../../../services/spinner/spinnerService";

export const mapTagGroupToForm = (
  group: TagGroupDto,
  pickedActions: IMessageWidgetConfig[]
): ExistingTagGroupFormType => {
  return new FormState<ExistingTagGroupFormFields>({
    id: new FieldState(group.id),
    tagIds: new FieldState(group.tags).validators(validateArrayNonEmpty),
    active: new FieldState(group.active),
    showInConversationDetails: new FieldState(group.showInConversationDetails),
    attachmentsEnabled: new FieldState(group.attachmentsEnabled),
    name: new FieldState(group.name).validators(validateRequired, validateNoWhitespace),
    activeHours: new FieldState(getWorkingWeekIntervals(group.activeHours)),
    messageWidgets: new FieldState<IMessageWidgets>({
      picked: pickedActions,
      toEnable: [],
      toDisable: []
    })
  });
};

export const createNewTagGroupForm = (): NewTagGroupFormType => {
  return new FormState<NewTagGroupFormFields>({
    name: new FieldState("").validators(validateRequired, validateNoWhitespace),
    tagIds: new FieldState<string[]>([]).validators(validateArrayNonEmpty)
  });
};

export const copyExistingTagGroupForm = (
  form: ExistingTagGroupFormType
): ExistingTagGroupFormType => {
  return new FormState<ExistingTagGroupFormFields>({
    id: new FieldState(form.$.id.$),
    tagIds: new FieldState(form.$.tagIds.$).validators(validateArrayNonEmpty),
    active: new FieldState(form.$.active.$),
    showInConversationDetails: new FieldState(form.$.showInConversationDetails.$),
    attachmentsEnabled: new FieldState(form.$.attachmentsEnabled.$),
    name: new FieldState(form.$.name.$).validators(validateRequired, validateNoWhitespace),
    activeHours: new FieldState(
      form.$.activeHours.$.map((hours) => ({ from: hours.from, to: hours.to }))
    ),
    messageWidgets: new FieldState(form.$.messageWidgets.$)
  });
};

export const mapFormToTagGroup = (orgId: string, form: ExistingTagGroupFormType): TagGroupDto => {
  return {
    id: form.$.id.$,
    orgId,
    activeHours: calculateWorkingHours(form.$.activeHours.$),
    attachmentsEnabled: form.$.attachmentsEnabled.$,
    active: form.$.active.$,
    showInConversationDetails: form.$.showInConversationDetails.$,
    tags: form.$.tagIds.$,
    name: form.$.name.$
  };
};

export const mapNewFormToCreateForm = (form: NewTagGroupFormType): CreateTagGroupForm => {
  return { name: form.$.name.$, tagIds: [...form.$.tagIds.$] };
};

export const mapExistingFormToUpdateForm = (form: ExistingTagGroupFormType): UpdateTagGroupForm => {
  return {
    name: form.$.name.$,
    tagIds: form.$.tagIds.$,
    active: form.$.active.$,
    showInConversationDetails: form.$.showInConversationDetails.$,
    attachmentsEnabled: form.$.attachmentsEnabled.$,
    activeHours: calculateWorkingHours(form.$.activeHours.$)
  };
};

export const validateTagGroupNoTagsConflict = (
  updatedItem: ExistingTagGroupFormType,
  items: ExistingTagGroupFormType[]
): boolean => {
  const sortedTagIds = updatedItem.$.tagIds.$.slice().sort();

  return !items.find(
    (item) =>
      item.$.id.$ !== updatedItem.$.id.$ && isEqual(sortedTagIds, item.$.tagIds.$.slice().sort())
  );
};

export const validateTagGroupsNoTagsConflict = (items: ExistingTagGroupFormType[]): boolean => {
  return items.every((item) => validateTagGroupNoTagsConflict(item, items));
};

export const validateNewTagGroupNoTagConflict = (
  newItem: NewTagGroupFormType,
  items: ExistingTagGroupFormType[]
): boolean => {
  const sortedTags = items.map((item) => item.$.tagIds.$.sort());
  const sortedNewItemTags = newItem.$.tagIds.$.sort();

  return !sortedTags.find((el) => isEqual(sortedNewItemTags, el));
};

export const validateNewTagGroupNoNameConflict = (
  newItem: NewTagGroupFormType,
  items: ExistingTagGroupFormType[]
): boolean => {
  const existingNames = items.map((i) => i.$.name.$);
  const name = newItem.$.name.$;

  return !existingNames.find((n) => n === name);
};

export const validateTagGroupNoNameConflict = (
  updatedItem: ExistingTagGroupFormType,
  items: ExistingTagGroupFormType[]
): boolean => {
  return !items.find((i) => i.$.id.$ !== updatedItem.$.id.$ && i.$.name.$ === updatedItem.$.name.$);
};

const groupByAction = (current: IMessageWidgetConfig[], initialPicked: IMessageWidgetConfig[]) => {
  const toDisable = initialPicked.filter(({ id }) => {
    return !current.find((el) => el.id === id);
  });

  const toEnable = current.filter((el) => !initialPicked.find(({ id }) => el.id === id));

  return { picked: current, toDisable, toEnable };
};

export const handleMessageWidgetPick = (
  current: IMessageWidgetConfig[],
  initialPicked: IMessageWidgetConfig[],
  selected: IMessageWidgetConfig
) => {
  const picked = [...current, selected];

  return groupByAction(picked, initialPicked);
};

export const handleMessageWidgetDelete = (
  current: IMessageWidgetConfig[],
  initialPicked: IMessageWidgetConfig[],
  idToDelete: string
) => {
  const picked = current.filter((action) => action.id !== idToDelete);

  return groupByAction(picked, initialPicked);
};

export const handleMessageWidgetsUpdate = async (
  client: SpinnerService,
  tagGroupId: string,
  toEnable: IMessageWidgetConfig[],
  toDisable: IMessageWidgetConfig[]
): Promise<IMessageWidgetConfig[]> => {
  const updated = await Promise.all([
    ...toEnable.map((messageWidget) =>
      client.updateMessageWidgetConfig(messageWidget.id, { ...messageWidget, tagGroupId })
    ),
    ...toDisable.map((messageWidget) =>
      client.updateMessageWidgetConfig(messageWidget.id, {
        ...messageWidget,
        tagGroupId: undefined
      })
    )
  ]);

  return updated.map((config) => MessageWidgetConfig.create(parseMessageWidgetConfig(config)));
};
